import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { IconPlus, IconLoader, IconAlert, IconCheck, IconMail, IconTrash } from "../components/Icons";
import Toggle from "../components/Toggle";
import PermissionsModal from "../components/PermissionsModal";

interface TenantUser {
  id: string;
  email: string;
  full_name: string | null;
  role: UserRole;
  is_active: boolean;
  email_verified_at: string | null;
  last_login_at: string | null;
  created_at: string;
  permissions?: Record<string, unknown>;
}

type UserRole = "owner" | "accountant" | "custom";

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString("cs-CZ") : "—";
}

export default function Users() {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState<UserRole>("custom");
  const [inviting, setInviting] = useState(false);
  const [permUser, setPermUser] = useState<TenantUser | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get<TenantUser[]>("/tenant/users");
      setUsers(data);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || t("users.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { void load(); }, [load]);

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    setErr(null); setOk(null);
    setInviting(true);
    try {
      const { data: created } = await api.post<TenantUser>("/tenant/users", {
        email: inviteEmail,
        full_name: inviteName || null,
        role: inviteRole,
      });
      setOk(t("users.inviteSent", { email: inviteEmail }));
      setInviteEmail(""); setInviteName(""); setInviteRole("custom");
      await load();
      if (created?.role === "custom") setPermUser(created);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || t("users.inviteError"));
    } finally {
      setInviting(false);
    }
  }

  async function patch(user: TenantUser, body: Partial<{ full_name: string; role: UserRole; is_active: boolean }>) {
    setErr(null); setOk(null);
    setBusy(user.id);
    try {
      await api.put(`/tenant/users/${user.id}`, body);
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || t("users.saveError"));
    } finally {
      setBusy(null);
    }
  }

  async function remove(user: TenantUser) {
    if (!window.confirm(t("users.deleteConfirm", { email: user.email }))) return;
    setErr(null); setOk(null);
    setBusy(user.id);
    try {
      await api.delete(`/tenant/users/${user.id}`);
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || t("users.deleteError"));
    } finally {
      setBusy(null);
    }
  }

  if (user && user.role !== "owner") {
    return <Navigate to="/" replace />;
  }
  if (loading) {
    return <div className="card" style={{ textAlign: "center", padding: 48 }}><IconLoader size={32} className="muted" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t("users.title")}</h1>
        <p>{t("users.subtitle")}</p>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{t("users.inviteTitle")}</h3>
        <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 16 }}>
          {t("users.inviteHint")}
        </p>
        <form onSubmit={invite} style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: "2 1 220px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>E-mail <IconMail size={14} className="muted" /></label>
            <input type="email" placeholder="kolega@firma.cz" value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)} required />
          </div>
          <div style={{ flex: "2 1 180px" }}>
            <label>{t("common.name")} <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 400 }}>({t("auth.optional")})</span></label>
            <input placeholder={t("auth.fullNamePh")} value={inviteName} onChange={(e) => setInviteName(e.target.value)} />
          </div>
          <div style={{ flex: "1 1 140px" }}>
            <label>{t("users.role")}</label>
            <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as UserRole)}>
              <option value="custom">{t("users.roleCustom")}</option>
              <option value="owner">{t("users.roleOwner")}</option>
              <option value="accountant">{t("users.roleAccountant")}</option>
            </select>
          </div>
          <button type="submit" className="primary" disabled={inviting}>
            {inviting ? <IconLoader size={18} /> : <><IconPlus size={18} /> {t("users.invite")}</>}
          </button>
        </form>
      </div>

      {err && (
        <div className="card" style={{
          marginBottom: 20, display: "flex", alignItems: "center", gap: 8,
          background: "var(--red-dim)", color: "var(--red)", fontSize: 13,
        }}>
          <IconAlert size={16} /> {err}
        </div>
      )}
      {ok && (
        <div className="card" style={{
          marginBottom: 20, display: "flex", alignItems: "center", gap: 8,
          background: "var(--green-dim)", color: "var(--green)", fontSize: 13,
        }}>
          <IconCheck size={16} /> {ok}
        </div>
      )}

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>{t("common.name")}</th>
              <th>E-mail</th>
              <th>{t("users.role")}</th>
              <th>{t("users.colVerified")}</th>
              <th>{t("users.colLastLogin")}</th>
              <th>{t("users.colCreated")}</th>
              <th>{t("common.active")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td style={{ fontWeight: 600 }}>{u.full_name || "—"}</td>
                <td>{u.email}</td>
                <td>
                  <select
                    value={u.role}
                    disabled={busy === u.id}
                    onChange={(e) => void patch(u, { role: e.target.value as UserRole })}
                    style={{ fontSize: 13, padding: "4px 8px" }}
                  >
                    <option value="owner">{t("users.roleOwner")}</option>
                    <option value="accountant">{t("users.roleAccountant")}</option>
                    <option value="custom">{t("users.roleCustom")}</option>
                  </select>
                </td>
                <td>
                  {u.email_verified_at
                    ? <span className="badge badge-green">{t("users.verified")}</span>
                    : <span className="badge badge-yellow">{t("users.pendingActivation")}</span>}
                </td>
                <td style={{ color: "var(--muted)", fontSize: 13 }}>{formatDate(u.last_login_at)}</td>
                <td style={{ color: "var(--muted)", fontSize: 13 }}>{formatDate(u.created_at)}</td>
                <td>
                  <Toggle
                    checked={u.is_active}
                    disabled={busy === u.id}
                    onChange={(checked) => void patch(u, { is_active: checked })}
                  />
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {u.role === "custom" && (
                    <button className="ghost" style={{ padding: "4px 10px", fontSize: 12 }}
                      disabled={busy === u.id} onClick={() => setPermUser(u)}>
                      {t("users.permissions")}
                    </button>
                  )}
                  <button className="ghost" style={{ padding: "4px 10px", fontSize: 12, color: "var(--red)" }}
                    disabled={busy === u.id} onClick={() => void remove(u)}>
                    <IconTrash size={14} /> {t("common.delete")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {permUser && (
        <PermissionsModal
          user={permUser}
          onSaved={() => void load()}
          onClose={() => setPermUser(null)}
        />
      )}
    </div>
  );
}
